import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertCircle, X } from "lucide-react";
import { getCsrfToken } from "../lib/auth";

/** Compact top-bar banner that surfaces stale data sources to the
 *  user. Driven by per-source thresholds in
 *  /api/notifications/source-freshness, configured in Settings →
 *  Notifications → Source freshness. Only renders when at least one
 *  source has status="stale" (past threshold, within active hours,
 *  not dismissed for the day).
 *
 *  Background: on 2026-05-20 Waqas's outlook-bridge connector went
 *  silent for 14h while the bridge stayed healthy. The Pulse
 *  anomaly card caught it but it was too quiet. This banner is the
 *  louder surface (C8). One tap routes to /today where the existing
 *  Pulse anomaly panel renders detail; dismiss hides it for the
 *  rest of the user's local day.
 *
 *  Brand: NO em dashes; Memso lexicon; never use the word "brain"
 *  in user-facing copy. */
interface StaleSource {
  source_key: string;
  label: string;
  threshold_hours: number;
  last_event_at: string | null;
  hours_silent: number | null;
  status: "stale" | "ok" | "quiet" | "snoozed" | "outside_hours";
  dismissed_until: string | null;
}

const POLL_INTERVAL_MS = 60_000;

export function StaleSourceBanner() {
  const [alerts, setAlerts] = useState<StaleSource[] | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [dismissingKey, setDismissingKey] = useState<string | null>(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const fetchAlerts = async () => {
    try {
      const r = await fetch("/api/notifications/source-freshness/alerts", {
        credentials: "same-origin",
      });
      if (!r.ok) return;
      const data = (await r.json()) as { alerts: StaleSource[] };
      setAlerts(data.alerts ?? []);
    } catch {
      // Best-effort. A network hiccup keeps the banner in its last
      // state rather than masking a real stale alert with a fresh
      // empty list.
    }
  };

  useEffect(() => {
    void fetchAlerts();
    const id = window.setInterval(() => void fetchAlerts(), POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, []);

  const stale = (alerts ?? []).filter((a) => a.status === "stale");
  if (stale.length === 0 || collapsed) return null;

  // Hide on routes where the banner would compete for limited
  // top-bar space and the user is already on the surface that
  // shows the detail anyway.
  if (pathname.startsWith("/today")) return null;
  if (pathname.startsWith("/mail")) return null;

  const headline =
    stale.length === 1
      ? `${stale[0].label} silent for ${formatHoursSilent(stale[0].hours_silent)}`
      : `${stale.length} sources silent past their freshness window`;

  const subline =
    stale.length === 1
      ? `Threshold: ${stale[0].threshold_hours}h. Last event ${formatLastEventAt(stale[0].last_event_at)}.`
      : stale
          .slice(0, 3)
          .map((s) => `${s.label} (${formatHoursSilent(s.hours_silent)})`)
          .join(", ") + (stale.length > 3 ? `, +${stale.length - 3} more` : "");

  const handleDismiss = async (key: string) => {
    setDismissingKey(key);
    try {
      await fetch(
        `/api/notifications/source-freshness/dismiss/${encodeURIComponent(key)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...csrfHeaders() },
          credentials: "same-origin",
        },
      );
      await fetchAlerts();
    } finally {
      setDismissingKey(null);
    }
  };

  const handleDismissAll = async () => {
    for (const s of stale) {
      await fetch(
        `/api/notifications/source-freshness/dismiss/${encodeURIComponent(s.source_key)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...csrfHeaders() },
          credentials: "same-origin",
        },
      );
    }
    setCollapsed(true);
    await fetchAlerts();
  };

  return (
    <div
      role="region"
      aria-label="Source freshness alert"
      className="sticky top-0 z-30 w-full bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-100"
    >
      <div className="mx-auto max-w-screen-2xl flex items-center gap-3 px-3 py-2 text-sm">
        <AlertCircle className="w-4 h-4 flex-shrink-0" aria-hidden />
        <div className="flex-1 min-w-0">
          <div className="font-semibold leading-tight truncate">{headline}</div>
          <div className="text-xs text-amber-800/90 dark:text-amber-200/85 leading-tight mt-0.5 truncate">
            {subline}
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate("/today#pulse")}
          className="flex-shrink-0 rounded-md bg-amber-900/10 hover:bg-amber-900/20 dark:bg-amber-100/10 dark:hover:bg-amber-100/20 px-3 py-1 text-xs font-semibold"
        >
          View on Today
        </button>
        {stale.length === 1 ? (
          <button
            type="button"
            onClick={() => handleDismiss(stale[0].source_key)}
            disabled={dismissingKey === stale[0].source_key}
            className="flex-shrink-0 rounded-md bg-amber-900/10 hover:bg-amber-900/20 dark:bg-amber-100/10 dark:hover:bg-amber-100/20 px-3 py-1 text-xs font-semibold disabled:opacity-60"
            title="Hide this source for the rest of today"
          >
            Dismiss for today
          </button>
        ) : (
          <button
            type="button"
            onClick={handleDismissAll}
            className="flex-shrink-0 rounded-md bg-amber-900/10 hover:bg-amber-900/20 dark:bg-amber-100/10 dark:hover:bg-amber-100/20 px-3 py-1 text-xs font-semibold"
            title="Hide all of these sources for the rest of today"
          >
            Dismiss all for today
          </button>
        )}
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          className="flex-shrink-0 p-1 rounded-md hover:bg-amber-900/10 dark:hover:bg-amber-100/10"
          aria-label="Hide banner"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function formatHoursSilent(h: number | null): string {
  if (h == null) return "unknown";
  if (h < 1) {
    const m = Math.round(h * 60);
    return `${m}m`;
  }
  if (h < 24) return `${Math.round(h * 10) / 10}h`;
  const d = Math.floor(h / 24);
  const rem = Math.round(h % 24);
  return rem > 0 ? `${d}d ${rem}h` : `${d}d`;
}

function formatLastEventAt(iso: string | null): string {
  if (!iso) return "never";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(d);
  } catch {
    return iso;
  }
}

function csrfHeaders(): Record<string, string> {
  const t = getCsrfToken();
  return t ? { "x-csrf-token": t } : {};
}
